import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { config } from './config'
import { Loading } from './auth'

// ── Types ──────────────────────────────────────────────────────────────────

type ModelStat = {
  model_group: string
  model_description: string
  codes: string[]
  order_count: number
}

type ApiChild = {
  value: string | null
  label: string
  share: number
  count: number
  ess: number
  confidence: number | null
  is_argmax: boolean
  is_excluded: boolean
  single_options: string[]
}

type ApiTree = {
  dim: string | null
  terminal: boolean
  truncated: boolean
  hidden: number
  children: ApiChild[]
}

// Normalized node store — a flat map keyed by a path-derived id makes deep updates cheap.
type TNode = {
  id: string
  dim: string                 // 'model' for the root, else the dimension of this node's value
  value: string | null
  label: string
  share: number
  cars: number | null         // predicted cars allocated to this node (null = no demand context)
  count: number
  confidence: number | null
  isArgmax: boolean
  isExcluded: boolean         // the runner-up just below the cut — shown, not expandable
  terminal: boolean           // an option leaf — cannot expand further
  childIds: string[] | null   // null = never loaded
  hiddenCount: number         // children pruned from the tail under this node
  expanded: boolean
  loading: boolean
  path: [string, string][]    // committed (dim, value) from root down
}

type Nodes = Record<string, TNode>

// ── Build the tree straight from the prediction's blocks (exact, no fetching) ──

export type ExploreBlock = {
  packages: string[]
  single_options: string[]
  n_cars: number
  rows: { color: string | null; interior: string | null; rim: string | null; trim: string | null; quantity: number }[]
}

const _ROW_DIMS = ['color', 'interior', 'rim', 'trim'] as const

function _addRows(
  nodes: Nodes, parentId: string, parentPath: [string, string][],
  rows: ExploreBlock['rows'], depth: number, expanded: boolean,
  mk: (id: string, dim: string, value: string | null, label: string, cars: number, parentCars: number, path: [string, string][], expanded: boolean) => void,
  parentCars: number,
) {
  let d = depth
  while (d < _ROW_DIMS.length && rows.every(r => r[_ROW_DIMS[d]] == null)) d++
  if (d >= _ROW_DIMS.length) return               // no more dimensions → this node is a leaf
  const dim = _ROW_DIMS[d]
  const groups = new Map<string, ExploreBlock['rows']>()
  for (const r of rows) {
    const v = r[dim] ?? ''
    const arr = groups.get(v); if (arr) arr.push(r); else groups.set(v, [r])
  }
  for (const [v, gr] of groups) {
    const cars = gr.reduce((s, r) => s + r.quantity, 0)
    const id = `${parentId}/${dim}:${v}`
    const path = [...parentPath, [dim, v]] as [string, string][]
    mk(id, dim, v || null, '', cars, parentCars, path, expanded)
    nodes[parentId].childIds!.push(id)
    _addRows(nodes, id, path, gr, d + 1, expanded, mk, cars)
  }
}

export function buildBlockTree(
  blocks: ExploreBlock[], descriptions: Record<string, string>, modelLabel: string,
  focus: { packages: string[]; single_options: string[] },
): Nodes {
  const nodes: Nodes = {}
  const desc = (c: string | null) => (c ? descriptions[c] ?? c : '—')
  const mk = (id: string, dim: string, value: string | null, label: string, cars: number, parentCars: number, path: [string, string][], expanded: boolean) => {
    nodes[id] = {
      id, dim, value, label: label || desc(value), share: parentCars > 0 ? cars / parentCars : 1,
      cars, count: 0, confidence: null, isArgmax: false, isExcluded: false, terminal: false,
      childIds: [], hiddenCount: 0, expanded, loading: false, path,
    }
  }
  const total = blocks.reduce((s, b) => s + b.n_cars, 0)
  const focusBundle = [...focus.packages].sort().join('|')
  const focusOpts = [...focus.single_options].sort().join('+')

  mk('root', 'model', null, modelLabel, total, total, [], true)
  const byBundle = new Map<string, ExploreBlock[]>()
  for (const b of blocks) {
    const k = [...b.packages].sort().join('|')
    const arr = byBundle.get(k); if (arr) arr.push(b); else byBundle.set(k, [b])
  }
  for (const [bkey, bbs] of byBundle) {
    const bundleCars = bbs.reduce((s, b) => s + b.n_cars, 0)
    const bid = `root/bundle:${bkey}`
    mk(bid, 'bundle', bkey, bkey ? bkey.split('|').map(desc).join(' + ') : 'Base car · no package',
       bundleCars, total, [['bundle', bkey]], bkey === focusBundle)
    nodes.root.childIds!.push(bid)
    for (const b of bbs) {
      const sig = [...b.single_options].sort().join('+')
      const oid = `${bid}/options:${sig}`
      mk(oid, 'options', sig, b.single_options.length ? b.single_options.map(desc).join(' + ') : 'No extra options',
         b.n_cars, bundleCars, [['bundle', bkey], ['options', sig]], bkey === focusBundle && sig === focusOpts)
      nodes[bid].childIds!.push(oid)
      _addRows(nodes, oid, nodes[oid].path, b.rows, 0, bkey === focusBundle && sig === focusOpts, mk, b.n_cars)
    }
  }
  for (const n of Object.values(nodes)) {
    n.terminal = !n.childIds || n.childIds.length === 0
    if (n.childIds && n.childIds.length) {
      let top = n.childIds[0]
      for (const cid of n.childIds) if ((nodes[cid].cars ?? 0) > (nodes[top].cars ?? 0)) top = cid
      nodes[top].isArgmax = true
    }
  }
  return nodes
}

type Params = {
  recencyHalfLifeDays: number
  pkgWeight: number
  colorWeight: number
  essThreshold: number
  coverageTarget: number
}
const DEFAULTS: Params = { recencyHalfLifeDays: 180, pkgWeight: 2.0, colorWeight: 1.0, essThreshold: 200, coverageTarget: 0.85 }

// ── Layout constants ─────────────────────────────────────────────────────────

const NODE_W = 180
const NODE_H = 66
const GAP_X = 28
const LEVEL_H = 132

const pct = (n: number) => `${(n * 100).toFixed(0)}%`
const confColor = (c: number | null) =>
  c == null ? '#CBD5E0' : c >= 0.65 ? '#0653B6' : c >= 0.35 ? '#5A9BE0' : '#A0AEC0'
const DIM_LABEL: Record<string, string> = {
  model: 'Model', bundle: 'Package bundle', color: 'Color',
  interior: 'Interior', rim: 'Rim', trim: 'Trim', options: 'Options',
}

// ── Layout (tidy top-down; parents centered over their visible children) ───────

function layout(nodes: Nodes, rootId: string) {
  const pos: Record<string, { x: number; y: number }> = {}
  let cursor = 0
  let maxDepth = 0
  const place = (id: string, depth: number) => {
    maxDepth = Math.max(maxDepth, depth)
    const n = nodes[id]
    const kids = n.expanded && n.childIds ? n.childIds : []
    if (kids.length === 0) {
      pos[id] = { x: cursor, y: depth * LEVEL_H }
      cursor += NODE_W + GAP_X
    } else {
      kids.forEach(k => place(k, depth + 1))
      pos[id] = { x: (pos[kids[0]].x + pos[kids[kids.length - 1]].x) / 2, y: depth * LEVEL_H }
    }
  }
  place(rootId, 0)
  return { pos, width: Math.max(cursor, NODE_W), height: (maxDepth + 1) * LEVEL_H }
}

// ── Node card ──────────────────────────────────────────────────────────────

function NodeCard({ node, onClick }: { node: TNode; onClick: () => void }) {
  const excluded = node.isExcluded
  const expandable = !node.terminal && !excluded
  const cars = excluded ? null : node.cars
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onClick() }}
      onPointerDown={e => e.stopPropagation()}
      className="flex flex-col justify-between overflow-hidden bg-white px-2.5 py-2 text-left transition-colors"
      style={{
        // every predicted (kept) node is "selected" → blue; only the runner-up is dashed/grey
        width: NODE_W, height: NODE_H,
        border: excluded ? '1px dashed #CBD5E0' : '1px solid #0653B6',
        borderLeft: excluded ? '3px solid #E5E7EC' : `3px solid ${confColor(node.confidence)}`,
        boxShadow: node.isArgmax && !excluded ? '0 0 0 1px #0653B6' : 'none',
        opacity: excluded ? 0.72 : 1,
      }}
      title={excluded ? `${node.label} — top option not selected` : node.label}
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`line-clamp-2 text-[11px] font-bold leading-tight ${excluded ? 'text-[#999]' : 'text-black'}`}>{node.label}</span>
        {expandable && (
          <span className="mt-0.5 flex-shrink-0 text-[10px] text-[#999]">{node.expanded ? '▾' : '▸'}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {excluded ? (
          <>
            <span className="text-[13px] font-bold tabular-nums text-[#AAA]">{pct(node.share)}</span>
            <span className="text-[9px] uppercase tracking-[0.04em] text-[#BBB]">not selected</span>
          </>
        ) : cars != null ? (
          <>
            <span className="bg-[#EAF1FB] px-1 text-[11px] font-bold tabular-nums text-bmw-ocean">{cars} cars</span>
            <span className="text-[10px] tabular-nums text-[#999]">{pct(node.share)}</span>
          </>
        ) : (
          <>
            <span className="text-[13px] font-bold tabular-nums text-bmw-ocean">{pct(node.share)}</span>
            <span className="text-[10px] tabular-nums text-[#999]">· {node.count} orders</span>
          </>
        )}
        {!excluded && (node.loading
          ? <span className="ml-auto text-[9px] uppercase tracking-wide text-bmw-ocean">…</span>
          : node.expanded && node.hiddenCount > 0 && (
              <span className="ml-auto text-[9px] tabular-nums text-[#BBB]" title={`${node.hiddenCount} niche branches hidden`}>+{node.hiddenCount}</span>
            ))}
      </div>
    </button>
  )
}

// ── Main ────────────────────────────────────────────────────────────────────

export function AdminChainTree({ showControls = true, staticNodes }: {
  showControls?: boolean       // false hides the weighting knobs
  staticNodes?: Nodes          // prebuilt tree (from the prediction) — no fetching
} = {}) {
  const isStatic = !!staticNodes
  const [models, setModels] = useState<ModelStat[] | null>(null)
  const [modelErr, setModelErr] = useState<string | null>(null)
  const [group, setGroup] = useState('')
  const [params, setParams] = useState<Params>(DEFAULTS)

  const [nodes, setNodes] = useState<Nodes>(staticNodes ?? {})
  const [err, setErr] = useState<string | null>(null)

  // Pan / zoom transform — one state object; a ref mirrors it for the wheel listener.
  const [view, setView] = useState({ tx: 40, ty: 24, k: 1 })
  const viewRef = useRef(view)
  viewRef.current = view
  const viewportRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null)
  const didFit = useRef(false)

  const activeModel = useMemo<ModelStat | null>(
    () => models?.find(m => m.model_group === group) ?? null, [models, group])
  const set = <K extends keyof Params>(key: K, v: Params[K]) => setParams(p => ({ ...p, [key]: v }))

  // Prebuilt (static) tree: adopt it and re-fit; no fetching.
  useEffect(() => {
    if (staticNodes) { setNodes(staticNodes); didFit.current = false }
  }, [staticNodes])

  // Model catalog (picker mode only).
  useEffect(() => {
    if (isStatic) return
    fetch(`${config.apiUrl}/api/admin/stats`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => {
        const ms: ModelStat[] = (d.models ?? []).filter((m: ModelStat) => m.order_count > 0)
        setModels(ms)
        if (ms.length) setGroup(ms[0].model_group)
      })
      .catch(() => setModelErr('Could not load model catalog'))
  }, [isStatic])

  const fetchChildren = useCallback(async (path: [string, string][]): Promise<ApiTree> => {
    const res = await fetch(`${config.apiUrl}/api/tree`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        model_codes: activeModel!.codes,
        path: path.map(([dim, value]) => ({ dim, value })),
        recency_half_life_days: params.recencyHalfLifeDays,
        pkg_weight: params.pkgWeight,
        color_weight: params.colorWeight,
        ess_threshold: params.essThreshold,
        coverage_target: params.coverageTarget,
      }),
    })
    if (!res.ok) throw new Error(`tree failed (${res.status})`)
    return res.json()
  }, [activeModel, params])

  const childNodesFrom = (parent: TNode, data: ApiTree): { ids: string[]; created: Nodes } => {
    const created: Nodes = {}
    const ids: string[] = []
    for (const c of data.children) {
      const v = c.value ?? ''
      const id = `${parent.id}/${data.dim}:${v}`
      created[id] = {
        id, dim: data.dim ?? 'options', value: c.value, label: c.label,
        share: c.share, cars: null, count: c.count, confidence: c.confidence,
        isArgmax: c.is_argmax, isExcluded: !!c.is_excluded,
        terminal: data.terminal, childIds: null, hiddenCount: 0, expanded: false, loading: false,
        path: [...parent.path, [data.dim ?? 'options', v]],
      }
      ids.push(id)
    }
    return { ids, created }
  }

  // (Re)build the root whenever the model or weighting changes (fetch-driven modes only).
  useEffect(() => {
    if (isStatic || !activeModel) return
    didFit.current = false
    const root: TNode = {
      id: 'root', dim: 'model', value: null,
      label: activeModel.model_description, share: 1, cars: null, count: activeModel.order_count,
      confidence: null, isArgmax: false, isExcluded: false, terminal: false,
      childIds: null, hiddenCount: 0, expanded: false, loading: true, path: [],
    }
    setNodes({ root })
    setErr(null)
    let cancelled = false
    ;(async () => {
      try {
        // Open on the winning (argmax) spine to the leaf; everything else one click away.
        const all: Nodes = { root }
        const expand = async (id: string): Promise<string[]> => {
          const data = await fetchChildren(all[id].path)
          const { ids, created } = childNodesFrom(all[id], data)
          Object.assign(all, created)
          all[id] = { ...all[id], childIds: ids, hiddenCount: data.hidden, expanded: true, loading: false }
          return ids
        }
        let cur = await expand('root')
        for (let d = 0; d < 8 && !cancelled; d++) {
          const nextId = cur.find(cid => all[cid].isArgmax && !all[cid].isExcluded && !all[cid].terminal)
          if (!nextId) break
          cur = await expand(nextId)
        }
        if (!cancelled) setNodes({ ...all })
      } catch (e) {
        if (!cancelled) setErr((e as Error).message)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModel, params])

  const toggle = useCallback(async (id: string) => {
    const node = nodes[id]
    if (!node || node.terminal || node.isExcluded) return
    if (node.childIds) {                       // already loaded → just flip visibility
      setNodes(p => ({ ...p, [id]: { ...p[id], expanded: !p[id].expanded } }))
      return
    }
    setNodes(p => ({ ...p, [id]: { ...p[id], loading: true } }))
    try {
      const data = await fetchChildren(node.path)
      const { ids, created } = childNodesFrom(node, data)
      setNodes(p => ({ ...p, [id]: { ...p[id], childIds: ids, hiddenCount: data.hidden, expanded: true, loading: false }, ...created }))
    } catch (e) {
      setErr((e as Error).message)
      setNodes(p => ({ ...p, [id]: { ...p[id], loading: false } }))
    }
  }, [nodes, fetchChildren])

  // Layout over the currently-visible tree.
  const { pos, width, height } = useMemo(
    () => (nodes.root ? layout(nodes, 'root') : { pos: {}, width: 0, height: 0 }),
    [nodes],
  )
  const visibleIds = Object.keys(pos)
  const edges = useMemo(() => {
    const out: { id: string; d: string; share: number; label: string; mx: number; my: number }[] = []
    for (const id of visibleIds) {
      const n = nodes[id]
      if (!n.expanded || !n.childIds) continue
      const p = pos[id]
      for (const cid of n.childIds) {
        const cp = pos[cid]
        if (!cp) continue
        const x1 = p.x + NODE_W / 2, y1 = p.y + NODE_H
        const x2 = cp.x + NODE_W / 2, y2 = cp.y
        const dy = (y2 - y1) / 2
        out.push({
          id: `${id}->${cid}`,
          d: `M ${x1} ${y1} C ${x1} ${y1 + dy} ${x2} ${y2 - dy} ${x2} ${y2}`,
          share: nodes[cid].share, label: pct(nodes[cid].share),
          mx: (x1 + x2) / 2, my: (y1 + y2) / 2,
        })
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, pos])

  const fit = useCallback(() => {
    const vp = viewportRef.current
    if (!vp || width === 0) return
    const scale = Math.min(vp.clientWidth / (width + 80), vp.clientHeight / (height + 80), 1)
    setView({ k: scale, tx: vp.clientWidth / 2 - ((pos.root?.x ?? 0) + NODE_W / 2) * scale, ty: 24 })
  }, [width, height, pos])

  // Auto-fit once the root's children first arrive.
  useEffect(() => {
    if (!didFit.current && nodes.root?.childIds) { didFit.current = true; fit() }
  }, [nodes, fit])

  // Native wheel zoom (needs a non-passive listener to preventDefault).
  useEffect(() => {
    const vp = viewportRef.current
    if (!vp) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = vp.getBoundingClientRect()
      const mx = e.clientX - rect.left, my = e.clientY - rect.top
      const { tx, ty, k } = viewRef.current
      const next = Math.min(2.5, Math.max(0.2, k * (1 - e.deltaY * 0.0012)))
      setView({ k: next, tx: mx - ((mx - tx) / k) * next, ty: my - ((my - ty) / k) * next })
    }
    vp.addEventListener('wheel', onWheel, { passive: false })
    return () => vp.removeEventListener('wheel', onWheel)
  }, [models])

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty }
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    setView(v => ({ ...v, tx: d.tx + (e.clientX - d.x), ty: d.ty + (e.clientY - d.y) }))
  }
  const endDrag = () => { drag.current = null }

  if (modelErr) return <p className="text-sm text-black">⚠ {modelErr}</p>
  if (!isStatic && !models) return <Loading />

  const lastDim = visibleIds.length
    ? nodes[visibleIds.reduce((a, b) => (pos[a].y >= pos[b].y ? a : b))].dim
    : 'model'

  return (
    <div className="space-y-4">
      {!isStatic && (
        <div>
          <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Chain tree</h1>
          <p className="mt-1 text-xs text-[#999]">
            The Bayesian chain as an explorable tree. Click a node to condition one dimension deeper —
            each branch's share is the weight the model gives it. Scroll to zoom, drag to pan.
          </p>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        {!isStatic && (
          <label className="block">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999]">Model</div>
            <select value={group} onChange={e => setGroup(e.target.value)}
              className="border border-[#CFD3DB] bg-white px-3 py-1.5 text-sm text-black">
              {(models ?? []).map(m => (
                <option key={m.model_group} value={m.model_group}>
                  {m.model_description} — {m.order_count.toLocaleString()} orders
                </option>
              ))}
            </select>
          </label>
        )}
        {showControls && ([
          ['Recency half-life', 'recencyHalfLifeDays', 30, 730, 10, (v: number) => `${v} d`],
          ['Package weight', 'pkgWeight', 0, 5, 0.1, (v: number) => v.toFixed(1)],
          ['Color weight', 'colorWeight', 0, 3, 0.1, (v: number) => v.toFixed(1)],
          ['ESS threshold', 'essThreshold', 5, 1000, 5, (v: number) => String(v)],
          ['Detail (coverage)', 'coverageTarget', 0.5, 1, 0.01, (v: number) => pct(v)],
        ] as const).map(([label, key, min, max, step, fmt]) => (
          <label key={key} className="block w-40">
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.05em] text-[#666]">{label}</span>
              <span className="tabular-nums text-[10px] font-bold text-black">{fmt(params[key])}</span>
            </div>
            <input type="range" min={min} max={max} step={step} value={params[key]}
              onChange={e => set(key, parseFloat(e.target.value))}
              className="h-1 w-full cursor-pointer accent-bmw-ocean" />
          </label>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => setView(v => ({ ...v, k: Math.max(0.2, v.k - 0.15) }))}
            className="h-7 w-7 border border-[#CFD3DB] text-sm font-bold text-[#666] hover:border-black hover:text-black">−</button>
          <span className="w-10 text-center text-[11px] tabular-nums text-[#999]">{Math.round(view.k * 100)}%</span>
          <button type="button" onClick={() => setView(v => ({ ...v, k: Math.min(2.5, v.k + 0.15) }))}
            className="h-7 w-7 border border-[#CFD3DB] text-sm font-bold text-[#666] hover:border-black hover:text-black">+</button>
          <button type="button" onClick={fit}
            className="ml-1 h-7 border border-[#CFD3DB] px-3 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black">Fit</button>
        </div>
      </div>

      {err && <p className="text-sm text-black">⚠ {err}</p>}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-[#999]">
        <span>Current depth: <span className="font-bold text-[#444]">{DIM_LABEL[lastDim] ?? lastDim}</span></span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2" style={{ background: '#0653B6' }} /> high conf.</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2" style={{ background: '#5A9BE0' }} /> medium</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2" style={{ background: '#A0AEC0' }} /> low</span>
        <span>· blue outline = the branch the model commits to · <span className="italic">dashed</span> = first option not selected · <span className="italic">orders</span> = exact matches</span>
      </div>

      {/* Canvas */}
      <div
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="relative h-[68vh] w-full cursor-grab overflow-hidden border border-[#E5E7EC] bg-[#FBFCFD] active:cursor-grabbing"
        style={{ touchAction: 'none' }}
      >
        {!nodes.root && <div className="p-4"><Loading /></div>}
        <div className="absolute left-0 top-0 origin-top-left"
          style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.k})` }}>
          <svg width={width} height={height} className="absolute left-0 top-0 overflow-visible">
            {edges.map(ed => (
              <g key={ed.id}>
                <path d={ed.d} fill="none" stroke="#B7C1D2" strokeWidth={1 + ed.share * 4} />
                <text x={ed.mx} y={ed.my} textAnchor="middle" dominantBaseline="middle"
                  className="fill-[#5A6577] font-bold" style={{ fontSize: 10, paintOrder: 'stroke', stroke: '#FBFCFD', strokeWidth: 3 }}>
                  {ed.label}
                </text>
              </g>
            ))}
          </svg>
          {visibleIds.map(id => (
            <div key={id} className="absolute" style={{ left: pos[id].x, top: pos[id].y }}>
              <NodeCard node={nodes[id]} onClick={() => toggle(id)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
